import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { SnackbarKey } from 'notistack';
import { useAuth } from '../../auth/useAuth';
import { DECISION_UNDO_MS } from '../../../contexts/DecisionQueueContext';
import { useSnackbar } from '../../../shared/utils/snackbar';
import { useDeferredActions } from '../../../shared/utils/useDeferredActions';
import { Actor, clearAlertReason, clearReasonText, ClearReason, logCheckIn } from '../tasks/taskActions';
import { ClientTasksResult } from '../tasks/useClientTasks';
import { ClientTask, DashboardClient, isAlertProgramme } from '../tasks/types';
import { CheckInDialog } from './CheckInDialog';

interface HeldClear {
  client: DashboardClient;
  task: ClientTask;
  clearReason: string;
}

export interface TaskActions {
  /** Managers and admins may clear alert-backed tasks (Firestore rules enforce the same). */
  canClear: (task: ClientTask) => boolean;
  clear: (client: DashboardClient, task: ClientTask, reason: ClearReason, note: string) => void;
  /** Tasks held for undo or being written: hide them so the list reflects the action at once. */
  hiddenTaskIds: Set<string>;
  openCheckIn: (client: DashboardClient, task: ClientTask) => void;
  /** Render once per page. */
  dialogs: React.ReactNode;
}

/**
 * Clear (with a short undo window, like the notification decision queue)
 * and the check-in shortcut, shared by the Inbox and Today views.
 *
 * Unlike the decision queue this lives inside a route, so leaving the page
 * commits whatever is still held rather than dropping it, and each Undo toast
 * closes the moment its write starts so it can never promise an undo that is
 * no longer possible.
 */
export function useTaskActions(tasks: Pick<ClientTasksResult, 'markAlertCleared' | 'markCheckInLogged'>): TaskActions {
  const { user, userRole } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const actorRef = useRef<Actor>({ uid: 'unknown', name: 'Unknown User' });
  actorRef.current = { uid: user?.uid || 'unknown', name: user?.displayName || user?.email || 'Unknown User' };
  const markCleared = useRef(tasks.markAlertCleared);
  markCleared.current = tasks.markAlertCleared;
  const toastKeys = useRef(new Map<string, SnackbarKey>());
  const mounted = useRef(true);
  const [writing, setWriting] = useState<Set<string>>(new Set());
  const [checkIn, setCheckIn] = useState<{ client: DashboardClient; task: ClientTask } | null>(null);
  // The dialog keeps showing the last client while it animates closed.
  const lastCheckIn = useRef(checkIn);
  if (checkIn) lastCheckIn.current = checkIn;

  const setWritingId = (id: string, on: boolean) => {
    if (!mounted.current) return;
    setWriting((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const { pending, schedule, cancel, flushAll } = useDeferredActions<HeldClear>(DECISION_UNDO_MS, async (id, held) => {
    const { client, task, clearReason } = held;
    const key = toastKeys.current.get(id);
    if (key !== undefined) closeSnackbar(key);
    toastKeys.current.delete(id);
    if (!isAlertProgramme(task.programme) || !task.alertReason) return;
    const programme = task.programme;
    setWritingId(id, true);
    try {
      await clearAlertReason(client, programme, task.alertReason, clearReason, actorRef.current);
      markCleared.current(client.clientId, programme, task.alertReason);
    } catch (error) {
      console.error('Dashboard: clearing an alert failed', error);
      enqueueSnackbar(`"${task.title}" for ${client.name} wasn't cleared, so it's back in the list.`, { variant: 'error' });
    } finally {
      setWritingId(id, false);
    }
  });
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  // Leaving the dashboard inside an undo window means "yes, do it": commit rather than drop.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      void flushAll();
    };
  }, [flushAll]);

  const canClear = useCallback(
    (task: ClientTask) => (userRole === 'ADMIN' || userRole === 'MANAGER') && Boolean(task.alertReason) && isAlertProgramme(task.programme),
    [userRole]
  );

  const clear = useCallback(
    (client: DashboardClient, task: ClientTask, reason: ClearReason, note: string) => {
      schedule(task.id, { client, task, clearReason: clearReasonText(reason, note) });
      const key = enqueueSnackbar(`Cleared: ${task.title.charAt(0).toLowerCase()}${task.title.slice(1)} for ${client.name}`, {
        autoHideDuration: DECISION_UNDO_MS,
        action: (snackbarKey) => (
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              closeSnackbar(snackbarKey);
              toastKeys.current.delete(task.id);
              if (task.id in pendingRef.current) cancel(task.id);
              else enqueueSnackbar('Already saved, so it can no longer be undone.', { variant: 'info' });
            }}
          >
            Undo
          </Button>
        ),
      });
      toastKeys.current.set(task.id, key);
    },
    [schedule, cancel, enqueueSnackbar, closeSnackbar]
  );

  const hiddenTaskIds = useMemo(() => new Set([...Object.keys(pending), ...Array.from(writing)]), [pending, writing]);

  const submitCheckIn = async (notes: string) => {
    if (!checkIn?.task.checkInType) return;
    const { client, task } = checkIn;
    const type = checkIn.task.checkInType;
    await logCheckIn(client.clientId, type, notes, actorRef.current);
    tasks.markCheckInLogged(client.clientId, type);
    setCheckIn(null);
    enqueueSnackbar(`${task.title.replace('Log the ', '').replace(/^./, (c) => c.toUpperCase())} saved for ${client.name}`, { variant: 'success' });
  };

  const shownCheckIn = checkIn ?? lastCheckIn.current;
  return {
    canClear,
    clear,
    hiddenTaskIds,
    openCheckIn: (client, task) => setCheckIn({ client, task }),
    dialogs: (
      <CheckInDialog
        open={checkIn !== null}
        clientName={shownCheckIn?.client.name ?? ''}
        type={shownCheckIn?.task.checkInType ?? '2_week'}
        onClose={() => setCheckIn(null)}
        onSubmit={submitCheckIn}
      />
    ),
  };
}
